<template>
  <div class="student-submissions-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="instructorName"
        current-portal="instructor"
        :disable-portal-link="!isVivaAdmin"
      />
    </div>

    <!-- Page Title Section -->
    <div class="instructor-info">
      <h1 class="viva-page-title">LIST OF STUDENT VIVAS</h1>
    </div>

    <!-- Submissions Card -->
    <div class="submissions-container">
      <div class="submissions-card">
        <!-- Assignment Info Header -->
        <div class="assignment-info-header">
          <div class="assignment-header-content">
            <div>
              <h3 class="assignment-title">Assignment: {{ assignment.title }}</h3>
              <p class="course-name">Assignment Type: {{ assignmentType }}</p>
              <p class="course-name">Course: {{ course.title }}</p>
              <p class="assignment-dates">
                <span class="date-label">SENT:</span> {{ formatDate(assignment.send_date) }} |
                <span class="date-label">DUE:</span> {{ formatDate(assignment.due_date) }}
              </p>
            </div>
            <button class="viva-btn viva-btn-green viva-btn-small" @click="openAddStudentModal('')" v-if="assignment.send_date">
              + ADD STUDENT
            </button>
          </div>
        </div>

        <!-- Submissions Table -->
        <div v-if="loading" class="loading-container">
          <i class="glyphicon glyphicon-refresh spinning"></i>
          <p>Loading student submissions...</p>
        </div>

        <div v-else-if="error" class="loading-container">
          <i class="glyphicon glyphicon-exclamation-sign"></i>
          <p>{{ error }}</p>
          <button class="viva-btn viva-btn-green" @click="loadData">Try Again</button>
        </div>

        <div v-else-if="!assignment.send_date" class="empty-state">
          <i class="glyphicon glyphicon-send"></i>
          <h3>Assignment Not Sent</h3>
          <p>Send this assignment to students to track their submissions!</p>
          <button class="viva-btn viva-btn-orange" @click="editAssignment">
            Edit Assignment
          </button>
        </div>

        <div v-else-if="submissions.length === 0" class="empty-state">
          <i class="glyphicon glyphicon-inbox"></i>
          <h3>No Submissions Yet</h3>
          <p>Student submissions will appear here once they complete their VIVA</p>
        </div>

        <div v-else class="submissions-table-wrapper">
          <div class="sort-toolbar">
            <label class="form-label-small" for="sortSelect">Sort By:</label>
            <select id="sortSelect" class="form-select" v-model="sortOption">
              <option value="name-asc">Alphabetical (A→Z)</option>
              <option value="name-desc">Alphabetical (Z→A)</option>
              <option value="vivascore-desc">VivaScore (High→Low)</option>
              <option value="vivascore-asc">VivaScore (Low→High)</option>
              <option value="date-asc">Completion Time (Earliest→Latest)</option>
              <option value="date-desc">Completion Time (Latest→Earliest)</option>
            </select>
            <span class="submission-count">{{ submittedCount }}/{{ totalStudentCount }} students completed</span>
          </div>

          <div class="table-scroll-container">
            <table class="submissions-table">
              <thead>
                <tr>
                  <th class="student-column">STUDENT</th>
                  <th class="status-column">STATUS</th>
                  <th class="date-column">TIME COMPLETED</th>
                  <th v-if="vivaScoreAvailable" class="vivascore-col-header">
                    <VivaTooltip :text="vivaScoreTooltip" placement="bottom">VIVASCORE <i class="fa fa-info-circle col-info-icon"></i></VivaTooltip>
                  </th>
                  <th v-if="vivaScoreAvailable" class="metrics-col-header">
                    <VivaTooltip :text="contentAlignmentTooltip" placement="bottom">CONTENT ALIGNMENT <i class="fa fa-info-circle col-info-icon"></i></VivaTooltip>
                  </th>
                  <th v-if="vivaScoreAvailable" class="metrics-col-header">
                    <VivaTooltip :text="responseFluencyTooltip" placement="bottom">RESPONSE FLUENCY <i class="fa fa-info-circle col-info-icon"></i></VivaTooltip>
                  </th>
                  <th v-if="vivaScoreAvailable && !isFirstAssignment" class="flag-col-header">TREND</th>
                  <th v-if="hasAnyStudentNote" class="note-column">NOTE FROM STUDENT</th>
                  <th class="comment-column">INSTRUCTOR COMMENT</th>
                  <th class="accommodation-column">ACCOM.</th>
                  <th class="actions-column">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="submission in sortedSubmissions"
                  :key="submission.student_id"
                  :class="{
                    'submitted-row': submission.submission_status === 'submitted',
                    'not-submitted-row': submission.submission_status !== 'submitted',
                    'not-sent-row': !submission.assignment_sent,
                    'active-overlay-row': activeStudentIds.indexOf(submission.student_id) !== -1
                  }"
                  @click="handleRowClick(submission)"
                >
                  <!-- Name → Student Progress link is commented out (not deleted) until
                       that page launches; we do want it back eventually.
                  <td
                    class="student-column"
                    :class="{ 'student-name-link': vivaScoreAvailable }"
                    @click.stop="vivaScoreAvailable ? goToStudentProgress(submission) : handleRowClick(submission)"
                  >
                    <span v-if="submission.student_name && submission.student_name.trim() !== ''" class="student-name">{{ submission.student_name }}</span>
                    <span class="student-email">{{ submission.student_email }}</span>
                  </td>
                  -->
                  <td
                    class="student-column"
                    @click.stop="handleRowClick(submission)"
                  >
                    <span v-if="submission.student_name && submission.student_name.trim() !== ''" class="student-name">{{ submission.student_name }}</span>
                    <span class="student-email">{{ submission.student_email }}</span>
                  </td>

                  <!-- Status column: video icon when the viva was taken, pink oval otherwise -->
                  <td class="status-column">
                    <img
                      v-if="submission.review_url_param"
                      src="/viva/favicon.png"
                      alt="View Video"
                      title="Watch by clicking"
                      class="video-icon"
                      @click.stop="openVideoReview(submission.review_url_param)"
                    />
                    <span v-else class="status-not-complete-badge">Not Complete</span>
                  </td>

                  <td class="date-column" :class="{ 'late-submission': isLateSubmission(submission) }">
                    <span v-if="!submission.assignment_sent" class="not-sent-badge">NOT SENT</span>
                    <span v-else>{{ formatSubmittedTime(submission.date_submitted) }}</span>
                  </td>

                  <!-- VivaScore column -->
                  <td v-if="vivaScoreAvailable" class="vivascore-col">
                    <span v-if="vivaScoresLoading" class="vivascore-spinner">
                      <i class="glyphicon glyphicon-refresh spinning"></i>
                    </span>
                    <template v-else-if="vivaScores[String(submission.student_id)] && vivaScores[String(submission.student_id)].vivascore.available">
                      <span
                        class="vivascore-z"
                        :style="{ color: vivaScoreColor(vivaScores[String(submission.student_id)].vivascore.vivascore_z) }"
                      >{{ formatZ(vivaScores[String(submission.student_id)].vivascore.vivascore_z) }}</span>
                      <MetricBar
                        :dots="[{ z: vivaScores[String(submission.student_id)].vivascore.vivascore_z, color: vivaScoreColor(vivaScores[String(submission.student_id)].vivascore.vivascore_z) }]"
                        :compact="true"
                      />
                    </template>
                    <span v-else class="vivascore-na">—</span>
                  </td>

                  <!-- Content Alignment column: number + bar for semantic_sim -->
                  <td v-if="vivaScoreAvailable" class="metrics-col">
                    <template v-if="!vivaScoresLoading && vivaScores[String(submission.student_id)] && vivaScores[String(submission.student_id)].vivascore.available">
                      <span
                        class="vivascore-z"
                        :style="{ color: vivaScoreColor(vivaScores[String(submission.student_id)].vivascore.metrics.semantic_sim ? vivaScores[String(submission.student_id)].vivascore.metrics.semantic_sim.z : null) }"
                      >{{ formatZ(vivaScores[String(submission.student_id)].vivascore.metrics.semantic_sim ? vivaScores[String(submission.student_id)].vivascore.metrics.semantic_sim.z : null) }}</span>
                      <MetricBar
                        :dots="[{ z: vivaScores[String(submission.student_id)].vivascore.metrics.semantic_sim ? vivaScores[String(submission.student_id)].vivascore.metrics.semantic_sim.z : null }]"
                        :compact="true"
                        title="Content Alignment"
                      />
                    </template>
                    <span v-else-if="!vivaScoresLoading" class="vivascore-na">—</span>
                  </td>

                  <!-- Response Fluency column: number + bar for response_fluency (words-per-second,
                       derived from response_duration_seconds — see shield/viva/vivascore.py) -->
                  <td v-if="vivaScoreAvailable" class="metrics-col">
                    <template v-if="!vivaScoresLoading && vivaScores[String(submission.student_id)] && vivaScores[String(submission.student_id)].vivascore.available">
                      <span
                        class="vivascore-z"
                        :style="{ color: vivaScoreColor(vivaScores[String(submission.student_id)].vivascore.metrics.response_fluency ? vivaScores[String(submission.student_id)].vivascore.metrics.response_fluency.z : null) }"
                      >{{ formatZ(vivaScores[String(submission.student_id)].vivascore.metrics.response_fluency ? vivaScores[String(submission.student_id)].vivascore.metrics.response_fluency.z : null) }}</span>
                      <MetricBar
                        :dots="[{ z: vivaScores[String(submission.student_id)].vivascore.metrics.response_fluency ? vivaScores[String(submission.student_id)].vivascore.metrics.response_fluency.z : null }]"
                        :compact="true"
                        title="Response Fluency"
                      />
                    </template>
                    <span v-else-if="!vivaScoresLoading" class="vivascore-na">—</span>
                  </td>

                  <!-- Trend column: green up / orange down arrow vs previous assignment -->
                  <td v-if="vivaScoreAvailable && !isFirstAssignment" class="flag-col">
                    <span
                      v-if="!vivaScoresLoading"
                      class="trend-arrow"
                      :style="{ color: trendColor(submission.student_id) }"
                      :title="trendTitle(submission.student_id)"
                    >{{ trendArrow(submission.student_id) }}</span>
                  </td>

                  <td v-if="hasAnyStudentNote" class="note-column">
                    <span
                      v-if="submission.student_note"
                      class="note-indicator"
                      :title="submission.student_note"
                      @click.stop="showStudentNote(submission)"
                    >{{ submission.student_note }}</span>
                  </td>

                  <td class="comment-column">
                    <template v-if="(submission.grade !== null && submission.grade !== undefined) || submission.instructor_comment">
                      <span class="instructor-grade">{{ formatGrade(submission.grade) }}</span>
                      <span
                        v-if="submission.instructor_comment"
                        class="instructor-comment-text"
                        :title="submission.instructor_comment"
                      >{{ submission.instructor_comment }}</span>
                    </template>
                  </td>

                  <td class="accommodation-column">
                    <span
                      v-if="submission.text_questions_enabled"
                      class="accommodation-badge active"
                      title="Text questions enabled"
                    >TXT</span>
                    <span
                      v-if="submission.time_multiplier > 1"
                      class="accommodation-badge active"
                      title="Extra time: 1.5x"
                    >1.5x</span>
                    <span
                      v-if="!submission.text_questions_enabled && !(submission.time_multiplier > 1)"
                      class="accommodation-badge inactive"
                    >&mdash;</span>
                  </td>
                  <td class="actions-column">
                    <!-- Send button: only for students NOT yet sent -->
                    <button
                      v-if="!submission.assignment_sent"
                      class="action-btn send-btn"
                      @click.stop="openAddStudentModal(submission)"
                      title="Send VIVA to student"
                    >
                      <span class="glyphicon glyphicon-send"></span>
                    </button>

                    <!-- For sent students: always show both resend and reset -->
                    <template v-else>
                      <button
                        class="action-btn resend-btn"
                        @click.stop="openResendModal(submission)"
                        title="Resend to student"
                      >
                        <span class="glyphicon glyphicon-envelope"></span>
                      </button>
                      <button
                        class="action-btn reset-btn"
                        @click.stop="openResetModal(submission)"
                        title="Reset assignment - give student a second chance"
                      >
                        <span class="glyphicon glyphicon-refresh"></span>
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pdf-export-row">
            <button class="viva-btn viva-btn-orange viva-btn-small" @click="exportPdf" :disabled="pdfExporting">
              {{ pdfExporting ? 'Generating…' : 'Printable PDF' }}
            </button>
            <p v-if="pdfExportError" class="pdf-export-error">{{ pdfExportError }}</p>
          </div>
        </div>

        <!-- VivaScore Trend (violin plots) — commented out (not deleted) until a few
             more tweaks land; we do want this back eventually.
        <div v-if="trajectoryReady" class="trajectory-container">
          <div class="trajectory-card">
            <h3 class="trajectory-title">VivaScore Trend</h3>
            <div>
              <div class="violin-chart-wrap" :style="violinWrapStyle">
                <VivaViolinChart
                  mode="assignments"
                  :assignments="violinAssignments"
                  :activeStudentIds="activeStudentIds"
                  :classAvgHighlighted="classAvgHighlighted"
                  :studentColors="studentColorMap"
                  @student-clicked="toggleStudent"
                  @avg-clicked="toggleClassAvg"
                />
              </div>
              <p class="legend-instructions">Each violin shows the spread of VivaScores for one assignment. The black dot is the class average. Click a student name to see their trend line. Click again to remove.</p>
              <div class="violin-legend-wrap">
              <div class="violin-legend">
                <span
                  class="legend-chip legend-chip--avg"
                  :class="{ 'legend-chip--active': classAvgHighlighted }"
                  @click="toggleClassAvg"
                >&#9679; Class Avg</span>
                <span
                  v-for="(s, idx) in legendStudents"
                  :key="s.id"
                  class="legend-chip"
                  :class="{ 'legend-chip--active': activeStudentIds.indexOf(s.id) !== -1 }"
                  :style="{ '--chip-color': legendColor(idx) }"
                  @click="toggleStudent(s.id)"
                >&#9679; {{ s.name }}</span>
              </div>
              </div>
            </div>
          </div>
        </div>
        -->

        <!-- Shared Link Section -->
        <div v-if="assignment.send_date" class="shared-link-section">
          <div class="shared-link-content">
            <div class="shared-link-info">
              <i class="fa fa-link"></i>
              <div>
                <h4>Shareable Assignment Link</h4>
                <p>Post this link on Canvas or your LMS - it works for all students in this course:</p>
              </div>
            </div>
            <div class="link-display-container">
              <input
                ref="sharedLinkInput"
                readonly
                :value="sharedLink"
                class="link-input"
                @click="selectLink"
              />
              <button
                class="viva-btn viva-btn-orange viva-btn-small copy-btn"
                @click="copySharedLink"
              >
                <i class="fa" :class="linkCopied ? 'fa-check' : 'fa-copy'"></i>
                {{ linkCopied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
            <p class="link-note">
              <i class="fa fa-info-circle"></i>
              Only students listed below can access this link. First-time users will need to reset their password to create their account.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Student Modal -->
    <div v-if="showAddStudentModal" class="viva-modal-backdrop" @click.self="closeAddStudentModal">
      <div class="viva-modal-content">
        <div class="viva-modal-header">
          <h2>{{ isSendingToExisting ? 'Send VIVA to Student' : 'Add Student to VIVA Assignment' }}</h2>
        </div>
        <div class="viva-modal-body">
          <div class="form-group">
            <label class="form-label">STUDENT EMAIL:</label>
            <input
              type="email"
              class="form-input"
              v-model="newStudentEmail"
              placeholder="Enter student email"
              :readonly="isSendingToExisting"
            />
          </div>
          <div class="form-group">
            <label class="form-label">OPTIONAL NOTE:</label>
            <textarea
              class="form-textarea"
              v-model="newStudentNote"
              placeholder="Add a note for this student (optional)"
              rows="3"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">ACCOMMODATIONS:</label>
            <div class="accommodation-toggles-modal">
              <button
                type="button"
                @click="newStudentAccommodations.textQuestionsEnabled = !newStudentAccommodations.textQuestionsEnabled"
                :class="['accommodation-btn', newStudentAccommodations.textQuestionsEnabled ? 'active' : '']"
                title="Toggle text questions for deaf/hard of hearing"
              >
                Text Questions: {{ newStudentAccommodations.textQuestionsEnabled ? 'ON' : 'OFF' }}
              </button>
              <button
                type="button"
                @click="toggleNewStudentExtraTime"
                :class="['accommodation-btn', parseFloat(newStudentAccommodations.timeMultiplier) > 1 ? 'active' : '']"
                title="Toggle extra time (1.5x)"
              >
                Extra Time: {{ parseFloat(newStudentAccommodations.timeMultiplier) > 1 ? '1.5x' : 'OFF' }}
              </button>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <label class="notify-students-label">
            <input type="checkbox" v-model="notifyNewStudent" />
            Send email notification
          </label>
          <button class="viva-btn viva-btn-gray viva-btn-small" @click="closeAddStudentModal">
            CANCEL
          </button>
          <button class="viva-btn viva-btn-green viva-btn-small" @click="addStudent" :disabled="addingStudent">
            {{ addingStudent ? 'ADDING...' : (isSendingToExisting ? 'SEND VIVA' : 'ADD TO COURSE & SEND VIVA') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Resend Modal -->
    <div v-if="showResendModal" class="viva-modal-backdrop" @click.self="closeResendModal">
      <div class="viva-modal-content">
        <div class="viva-modal-header">
          <h2>Resend VIVA</h2>
        </div>
        <div class="viva-modal-body">
          <p style="margin-bottom: 15px;">
            Resending to: <strong>{{ selectedSubmission?.student_email }}</strong>
          </p>
          <div class="form-group">
            <label class="form-label">NOTE FOR STUDENT:</label>
            <textarea
              class="form-textarea"
              v-model="resendNote"
              placeholder="e.g., 'Please ignore the due date' or 'Second chance to complete'"
              rows="4"
            ></textarea>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button class="viva-btn viva-btn-gray viva-btn-small" @click="closeResendModal">
            CANCEL
          </button>
          <button class="viva-btn viva-btn-orange viva-btn-small" @click="resendToStudent" :disabled="resending">
            {{ resending ? 'SENDING...' : 'RESEND' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reset Assignment Modal -->
    <div v-if="showResetModal" class="viva-modal-backdrop" @click.self="closeResetModal">
      <div class="viva-modal-content">
        <div class="viva-modal-header">
          <h2>Reset Assignment</h2>
        </div>
        <div class="viva-modal-body">
          <div class="warning-box">
            <p class="warning-icon">⚠️</p>
            <p class="warning-text"><strong>WARNING: This action will override the student's previous submission.</strong></p>
          </div>
          <p style="margin-top: 20px; margin-bottom: 15px;">
            Resetting the assignment for: <strong>{{ selectedSubmission?.student_email }}</strong>
          </p>
          <p><strong>What will happen:</strong></p>
          <ul style="margin-left: 20px; margin-bottom: 20px;">
            <template v-if="assignment.has_teacher_upload">
              <li>New questions will be automatically generated</li>
              <li>Student will be able to record again with new questions</li>
            </template>
            <template v-else>
              <li>Student will need to upload their work again</li>
              <li>New questions will be generated from the new upload</li>
            </template>
            <li>Student's status will be reset to "not submitted"</li>
          </ul>
          <p style="color: #dc3545; font-weight: 600;">This will give the student a second chance to complete the assignment.</p>
          <div class="form-group" style="margin-top: 20px;">
            <label class="form-label">ACCOMMODATIONS:</label>
            <div class="accommodation-toggles-modal">
              <button
                type="button"
                @click="resetAccommodations.textQuestionsEnabled = !resetAccommodations.textQuestionsEnabled"
                :class="['accommodation-btn', resetAccommodations.textQuestionsEnabled ? 'active' : '']"
                title="Toggle text questions for deaf/hard of hearing"
              >
                Text Questions: {{ resetAccommodations.textQuestionsEnabled ? 'ON' : 'OFF' }}
              </button>
              <button
                type="button"
                @click="toggleResetExtraTime"
                :class="['accommodation-btn', parseFloat(resetAccommodations.timeMultiplier) > 1 ? 'active' : '']"
                title="Toggle extra time (1.5x)"
              >
                Extra Time: {{ parseFloat(resetAccommodations.timeMultiplier) > 1 ? '1.5x' : 'OFF' }}
              </button>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button class="viva-btn viva-btn-gray viva-btn-small" @click="closeResetModal">
            CANCEL
          </button>
          <button class="viva-btn viva-btn-orange viva-btn-small" @click="resetAssignment" :disabled="resetting">
            {{ resetting ? 'RESETTING...' : 'CONFIRM RESET' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Student Note Modal -->
    <div v-if="showStudentNoteModal" class="viva-modal-backdrop" @click.self="closeStudentNoteModal">
      <div class="viva-modal-content">
        <div class="viva-modal-header">
          <h2>Student Note</h2>
        </div>
        <div class="viva-modal-body">
          <p style="white-space: pre-wrap; font-size: 15px; line-height: 1.6;">{{ studentNoteContent }}</p>
        </div>
        <div class="viva-modal-footer">
          <button class="viva-btn viva-btn-green viva-btn-small" @click="closeStudentNoteModal">
            CLOSE
          </button>
        </div>
      </div>
    </div>

    <!-- Back Link -->
    <div class="back-section">
      <a href="#" @click.prevent="goBack" class="back-link">
        <span>←</span> Back to Assignments
      </a>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import env from '../utils/env.js';
import { handleApiError } from '../utils/errorHandler.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';
import MetricBar from '@/components/MetricBar.vue';
import VivaViolinChart from '@/components/VivaViolinChart.vue';
import VivaTooltip from '@/components/VivaTooltip.vue';
import { METRICS, VIVASCORE_COLORS, VIVASCORE_TOOLTIP } from '../utils/vivascore.js';

export default {
  name: 'StudentSubmissions',
  components: { ProfileDropdown, MetricBar, VivaViolinChart, VivaTooltip },

  data() {
    return {
      assignment: {},
      course: {},
      submissions: [],
      loading: true,
      error: null,
      instructorName: '',
      isVivaAdmin: false,
      // Modal state
      showAddStudentModal: false,
      showResendModal: false,
      selectedSubmission: null,
      // Add student form
      newStudentEmail: '',
      newStudentNote: '',
      addingStudent: false,
      isSendingToExisting: false,
      notifyNewStudent: true,
      // Resend form
      resendNote: '',
      resending: false,
      // Reset form
      showResetModal: false,
      resetting: false,
      // Shared link
      linkCopied: false,
      // Table sorting
      sortOption: 'name-asc',
      // PDF export
      pdfExporting: false,
      pdfExportError: '',
      // Add student accommodations
      newStudentAccommodations: {
        textQuestionsEnabled: false,
        timeMultiplier: '1.00'
      },
      // Reset accommodations
      resetAccommodations: {
        textQuestionsEnabled: false,
        timeMultiplier: '1.00'
      },
      // Student note modal
      showStudentNoteModal: false,
      studentNoteContent: '',
      // Polling
      pollInterval: null,
      // VivaScore state
      // vivaScoreAvailable mirrors the endpoint's page-level `available` (feature flag +
      // population floor). Defaults false so no VivaScore UI flashes before the data
      // confirms the feature is on; every VivaScore element in the template gates on it.
      vivaScoreAvailable: false,
      vivaScores: {},
      vivaScoresLoading: true,
      vivaScoresError: null,
      isFirstAssignment: false,
      trajectory: null,
      activeStudentIds: [],
      classAvgHighlighted: true,
      // VivaScore metadata constants
      metrics: METRICS,
      vivaScoreTooltip: VIVASCORE_TOOLTIP,
      // Distinct colors for the violin legend — one per student, cycles if class is large
      legendColors: [
        '#4E79A7', '#F28E2B', '#59A14F', '#B07AA1',
        '#9C755F', '#BAB0AC', '#76B7B2', '#FF9DA7',
      ],
    };
  },

  computed: {
    assignmentType() {
      return this.assignment.has_teacher_upload ? 'Reading' : 'Writing';
    },

    // Whole "Note from Student" column is omitted when nobody in the table left one.
    hasAnyStudentNote() {
      return this.submissions.some(s => s.student_note);
    },

    totalStudentCount() {
      return this.submissions.length;
    },

    submittedCount() {
      return this.submissions.filter(s => s.is_submitted).length;
    },

    // Tooltip copy for the split-out metric columns, reusing the same per-metric
    // wording defined in utils/vivascore.js (METRICS) rather than writing new copy.
    contentAlignmentTooltip() {
      const m = this.metrics.find(m => m.key === 'semantic_sim');
      return m ? m.tooltip : '';
    },

    responseFluencyTooltip() {
      const m = this.metrics.find(m => m.key === 'response_fluency');
      return m ? m.tooltip : '';
    },

    sharedLink() {
      if (!this.assignment.id) return '';
      return `${env.serverAddr}/viva/student/assignment/${this.assignment.id}`;
    },

    // Table sort order — driven by the "Sort By" dropdown. Sourced independently
    // from `submissions` so the violin chart's own student ordering/coloring
    // (legendStudents, violinAssignments) is unaffected by table sort.
    sortedSubmissions() {
      const list = this.submissions.slice();
      const key = s => (s.student_last_name && s.student_last_name.trim() !== '')
        ? s.student_last_name
        : ((s.student_name && s.student_name.trim() !== '') ? s.student_name : s.student_email);
      switch (this.sortOption) {
        case 'name-asc':
          return list.sort((a, b) => key(a).localeCompare(key(b)));
        case 'name-desc':
          return list.sort((a, b) => key(b).localeCompare(key(a)));
        case 'vivascore-desc':
          return this._sortByVivaScore(list, 'desc');
        case 'vivascore-asc':
          return this._sortByVivaScore(list, 'asc');
        case 'date-asc':
          return this._sortByCompletionTime(list, 'asc');
        case 'date-desc':
          return this._sortByCompletionTime(list, 'desc');
        default:
          return list;
      }
    },

    trajectoryReady() {
      if (!this.vivaScoreAvailable || !this.trajectory || this.vivaScoresLoading) return false;
      return this.trajectory.assignments.length >= 1;
    },

    // Students who appear in the violin chart — used to build the legend chips.
    // Only submitted students have z-scores, so only they appear in the violins.
    legendStudents() {
      return this.submissions
        .filter(s => s.is_submitted)
        .map(s => ({ id: s.student_id, name: s.student_name || s.student_email }));
    },

    // Maps student_id → legend color, passed to VivaViolinChart for per-student trend lines.
    studentColorMap() {
      const map = {};
      this.legendStudents.forEach((s, idx) => {
        map[s.id] = this.legendColors[idx % this.legendColors.length];
      });
      return map;
    },

    // Transforms trajectory data into the shape VivaViolinChart Mode B expects:
    // [{ label, students: [{ id, name, z }] }] — one entry per assignment.
    violinAssignments() {
      if (!this.trajectory) return [];
      return this.trajectory.assignments.map((a, i) => ({
        label: (() => { const t = a.title || ''; return `${a.label}: ${t.length > 20 ? t.slice(0, 19) + '…' : t}`; })(),
        students: this.submissions
          .filter(s => s.is_submitted)
          .map(s => ({
            id: s.student_id,
            name: s.student_name || s.student_email,
            z: (this.trajectory.student_scores[String(s.student_id)] || [])[i] ?? null,
          }))
          .filter(s => s.z !== null),
      }));
    },

    // Caps the rendered chart at half the viewport height. The SVG fills its
    // wrapper's width and derives height from the viewBox aspect ratio
    // (width : height = leftOffset + n·colW : viewBoxH, matching VivaViolinChart),
    // so limiting width to 50vh · aspect limits height to 50vh. calc() keeps it
    // live on window resize with no JS listeners.
    violinWrapStyle() {
      const leftOffset = 44;   // matches VivaViolinChart's leftOffset
      const colW = 112;        // matches VivaViolinChart's colW
      const viewBoxH = 220;    // matches VivaViolinChart's viewBox height
      const n = Math.max(this.violinAssignments.length, 1);
      const aspect = (leftOffset + n * colW) / viewBoxH;
      return { maxWidth: `calc(75vh * ${aspect.toFixed(4)})` };
    },
  },

  mounted() {
    const statusData = getStatusData();
    if (statusData) {
      this.instructorName = statusData.student_name || '';
      this.isVivaAdmin = statusData.is_viva_admin || false;
    }
    this.loadData();
    // Start silent polling every 60 seconds
    this.pollInterval = setInterval(() => this.loadDataSilent(), 60000);
  },

  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },

  methods: {
    async loadData() {
      this.loading = true;
      this.error = null;
      const assignmentId = this.$route.params.id;

      try {
        const response = await apiClient.get(`/api/viva/instructor/assignment/${assignmentId}/submissions/`);

        if (response.data) {
          this.assignment = response.data.assignment || {};

          // Backend now returns course info in assignment object
          this.course = {
            id: response.data.assignment.course_id,
            title: response.data.assignment.course_title
          };

          // Set review_url_param to null for non-submitted to hide the video icon
          this.submissions = response.data.submissions.map(submission => ({
            ...submission,
            review_url_param: submission.is_submitted ? submission.review_url_param : null
          }));
          this.loadVivaScores();
        }
      } catch (error) {
        console.error('Failed to load submissions:', error);
        this.error = handleApiError(error, this.$router, 'Failed to load submissions. Please try again.');

        if (error.response && error.response.status === 404) {
          alert('Assignment not found');
          this.$router.push({ name: 'instructor-dashboard' });
        }
      } finally {
        this.loading = false;
      }
    },

    async loadDataSilent() {
      const assignmentId = this.$route.params.id;

      try {
        const response = await apiClient.get(`/api/viva/instructor/assignment/${assignmentId}/submissions/`);

        if (response.data) {
          this.assignment = response.data.assignment || {};

          this.course = {
            id: response.data.assignment.course_id,
            title: response.data.assignment.course_title
          };

          this.submissions = response.data.submissions.map(submission => ({
            ...submission,
            review_url_param: submission.is_submitted ? submission.review_url_param : null
          }));
          this.loadVivaScores(true);
        }
      } catch (error) {
        // Silently ignore errors during polling
        console.log('Silent poll failed:', error);
      }
    },

    openVideoReview(vivaId) {
      window.open(`${env.serverAddr}/viva/review-student/?v=${vivaId}`, '_blank');
    },

    goBack() {
      if (this.assignment.course_id) {
        this.$router.push({
          name: 'course-assignments',
          params: { id: this.assignment.course_id }
        });
      } else {
        this.$router.push({ name: 'instructor-dashboard' });
      }
    },

    editAssignment() {
      if (this.assignment.id) {
        this.$router.push({ name: 'assignment-edit', params: { id: this.assignment.id } });
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    // Formats a UTC submission datetime in the assignment's configured timezone
    // (falls back to the backend's default if the assignment has none), including
    // the timezone abbreviation so instructors know what they're looking at.
    formatSubmittedTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const timeZone = this.assignment.timezone || 'America/New_York';
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
        timeZoneName: 'short'
      }).format(date);
    },

    isLateSubmission(submission) {
      if (!submission.date_submitted || !this.assignment.due_date) {
        return false;
      }
      const submittedDate = new Date(submission.date_submitted);
      const dueDate = new Date(this.assignment.due_date);
      return submittedDate > dueDate;
    },

    formatGrade(grade) {
      if (grade === null || grade === undefined) {
        return '';
      }
      // Format to 2 decimal places, or as integer if it's a whole number
      const num = parseFloat(grade);
      return num % 1 === 0 ? num.toString() : num.toFixed(2);
    },

    selectLink() {
      // Select all text in the link input when clicked
      if (this.$refs.sharedLinkInput) {
        this.$refs.sharedLinkInput.select();
      }
    },

    async copySharedLink() {
      try {
        // Use modern clipboard API
        await navigator.clipboard.writeText(this.sharedLink);
        this.linkCopied = true;

        // Reset the copied state after 2 seconds
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);

        // Fallback: select the text so user can manually copy
        this.selectLink();

        // Try older method
        try {
          this.$refs.sharedLinkInput.select();
          document.execCommand('copy');
          this.linkCopied = true;

          setTimeout(() => {
            this.linkCopied = false;
          }, 2000);
        } catch (fallbackError) {
          console.error('Fallback copy also failed:', fallbackError);
        }
      }
    },

    // Add Student Modal Methods
    openAddStudentModal(submissionOrEmpty = '') {
      // Handle both submission object and empty string (for new student)
      if (typeof submissionOrEmpty === 'object' && submissionOrEmpty !== null) {
        // Existing student - use their email and accommodations
        this.newStudentEmail = submissionOrEmpty.student_email || '';
        this.isSendingToExisting = true;
        this.newStudentAccommodations = {
          textQuestionsEnabled: submissionOrEmpty.text_questions_enabled || false,
          timeMultiplier: submissionOrEmpty.time_multiplier ? submissionOrEmpty.time_multiplier.toString() : '1.00'
        };
      } else {
        // New student - use defaults
        this.newStudentEmail = '';
        this.isSendingToExisting = false;
        this.newStudentAccommodations = {
          textQuestionsEnabled: false,
          timeMultiplier: '1.00'
        };
      }
      this.newStudentNote = '';
      this.showAddStudentModal = true;
    },

    closeAddStudentModal() {
      this.showAddStudentModal = false;
      this.newStudentEmail = '';
      this.newStudentNote = '';
      this.isSendingToExisting = false;
      // Reset accommodations to defaults
      this.newStudentAccommodations = {
        textQuestionsEnabled: false,
        timeMultiplier: '1.00'
      };
    },

    toggleNewStudentExtraTime() {
      // Toggle between 1.0 and 1.5
      const current = parseFloat(this.newStudentAccommodations.timeMultiplier);
      this.newStudentAccommodations.timeMultiplier = current > 1 ? '1.00' : '1.50';
    },

    toggleResetExtraTime() {
      // Toggle between 1.0 and 1.5
      const current = parseFloat(this.resetAccommodations.timeMultiplier);
      this.resetAccommodations.timeMultiplier = current > 1 ? '1.00' : '1.50';
    },

    async addStudent() {
      if (!this.newStudentEmail.trim()) {
        alert('Please enter a student email');
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.newStudentEmail)) {
        alert('Please enter a valid email address');
        return;
      }

      this.addingStudent = true;

      try {
        await apiClient.post(`/api/viva/instructor/assignment/${this.assignment.id}/add-student/`, {
          student_email: this.newStudentEmail.trim(),
          note: this.newStudentNote.trim(),
          text_questions_enabled: this.newStudentAccommodations.textQuestionsEnabled,
          time_multiplier: parseFloat(this.newStudentAccommodations.timeMultiplier),
          notify_student: this.notifyNewStudent
        });

        alert(this.notifyNewStudent ? 'Student added and assignment sent successfully!' : 'Student added successfully!');
        this.closeAddStudentModal();
        // Reload data to show the new student
        await this.loadData();

      } catch (error) {
        console.error('Failed to add student:', error);
        // Show the specific error message from the backend
        const errorMsg = error.response?.data?.error || handleApiError(error, this.$router, 'Failed to add student');
        if (error.response && error.response.status !== 401) {
          alert(errorMsg);
        }
      } finally {
        this.addingStudent = false;
      }
    },

    // Resend Modal Methods
    openResendModal(submission) {
      this.selectedSubmission = submission;
      this.resendNote = '';
      this.showResendModal = true;
    },

    closeResendModal() {
      this.showResendModal = false;
      this.selectedSubmission = null;
      this.resendNote = '';
    },

    async resendToStudent() {
      if (!this.selectedSubmission) {
        return;
      }

      this.resending = true;

      try {
        await apiClient.post(`/api/viva/instructor/assignment/${this.assignment.id}/resend/`, {
          student_email: this.selectedSubmission.student_email,
          note: this.resendNote.trim()
        });

        alert('Assignment resent successfully!');
        this.closeResendModal();

      } catch (error) {
        console.error('Failed to resend assignment:', error);
        // Show the specific error message from the backend
        const errorMsg = error.response?.data?.error || handleApiError(error, this.$router, 'Failed to resend assignment');
        if (error.response && error.response.status !== 401) {
          alert(errorMsg);
        }
      } finally {
        this.resending = false;
      }
    },

    // Reset Modal Methods
    openResetModal(submission) {
      this.selectedSubmission = submission;
      // Pre-populate with student's current accommodations
      this.resetAccommodations = {
        textQuestionsEnabled: submission.text_questions_enabled || false,
        timeMultiplier: submission.time_multiplier ? submission.time_multiplier.toString() : '1.00'
      };
      this.showResetModal = true;
    },

    closeResetModal() {
      this.showResetModal = false;
      this.selectedSubmission = null;
      // Reset accommodations to defaults
      this.resetAccommodations = {
        textQuestionsEnabled: false,
        timeMultiplier: '1.00'
      };
    },

    async resetAssignment() {
      if (!this.selectedSubmission) {
        return;
      }

      this.resetting = true;

      try {
        await apiClient.post(`/api/viva/instructor/assignment/${this.assignment.id}/reset/`, {
          student_email: this.selectedSubmission.student_email,
          text_questions_enabled: this.resetAccommodations.textQuestionsEnabled,
          time_multiplier: parseFloat(this.resetAccommodations.timeMultiplier)
        });

        alert('Assignment reset successfully! Student can now re-record with new questions.');
        this.closeResetModal();
        // Reload data to show updated status
        await this.loadData();

      } catch (error) {
        console.error('Failed to reset assignment:', error);
        // Show the specific error message from the backend
        const errorMsg = error.response?.data?.error || handleApiError(error, this.$router, 'Failed to reset assignment');
        if (error.response && error.response.status !== 401) {
          alert(errorMsg);
        }
      } finally {
        this.resetting = false;
      }
    },

    showStudentNote(submission) {
      if (submission.student_note) {
        this.studentNoteContent = submission.student_note;
        this.showStudentNoteModal = true;
      }
    },

    closeStudentNoteModal() {
      this.showStudentNoteModal = false;
      this.studentNoteContent = '';
    },

    // silent: refresh data without touching the loading/error UI state — used by the
    // 60-second poll so already-rendered scores don't flicker, and a transient poll
    // failure doesn't blank data that is already on screen.
    async loadVivaScores(silent = false) {
      const assignmentId = this.$route.params.id;
      if (!silent) {
        this.vivaScoresLoading = true;
        this.vivaScoresError = null;
      }
      try {
        const res = await apiClient.get(`/api/viva/instructor/assignment/${assignmentId}/vivascore/`);
        this.vivaScoreAvailable = res.data.available === true;
        this.vivaScores = res.data.students;
        this.isFirstAssignment = res.data.is_first_assignment || false;
        this.trajectory = res.data.trajectory;
      } catch (err) {
        console.error('Failed to load VivaScore data:', err);
        if (!silent) {
          this.vivaScoreAvailable = false;
          this.vivaScoresError = 'VivaScore data unavailable';
        }
      } finally {
        if (!silent) {
          this.vivaScoresLoading = false;
        }
      }
    },

    handleRowClick(submission) {
      if (!submission.assignment_sent) {
        this.openAddStudentModal(submission);
      } else if (submission.is_submitted && this.trajectoryReady) {
        this.toggleStudent(submission.student_id);
      }
    },

    // Unused while the student-name → Student Progress link is commented out in the
    // template (see above); left in place for easy restoration once that page launches.
    goToStudentProgress(submission) {
      if (!submission.student_id) return;
      const route = this.$router.resolve({
        name: 'student-progress',
        params: { student_id: submission.student_id },
        query: { course_id: this.course.id }
      });
      window.open(route.href, '_blank');
    },

    toggleStudent(studentId) {
      const idx = this.activeStudentIds.indexOf(studentId);
      if (idx === -1) {
        this.activeStudentIds = [...this.activeStudentIds, studentId];
      } else {
        this.activeStudentIds = this.activeStudentIds.filter(id => id !== studentId);
      }
    },

    toggleClassAvg() {
      this.classAvgHighlighted = !this.classAvgHighlighted;
    },

    legendColor(idx) {
      return this.legendColors[idx % this.legendColors.length];
    },

    vivaScoreColor(z) {
      if (z === null || z === undefined) return 'var(--viva-grey)';
      return z >= 0 ? VIVASCORE_COLORS.above : VIVASCORE_COLORS.below;
    },

    formatZ(z) {
      if (z === null || z === undefined) return '—';
      return (z >= 0 ? '+' : '') + z.toFixed(2);
    },

    // Printable PDF export is generated server-side (see shield/viva/instructor_views.py
    // assignment_submissions_pdf) so the download never depends on a frontend PDF library
    // being present wherever this app is deployed. apiClient carries auth via a static
    // Authorization header (not cookies), so the file must be fetched as a blob and turned
    // into a download manually rather than just linking/navigating to the URL.
    async exportPdf() {
      this.pdfExporting = true;
      this.pdfExportError = '';
      try {
        const response = await apiClient.get(
          `/api/viva/instructor/assignment/${this.assignment.id}/submissions/pdf/`,
          {
            params: { sort: this.sortOption, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
            responseType: 'blob',
          }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.assignment.title || 'submissions'}-submissions.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error('PDF export failed:', e);
        this.pdfExportError = await this._extractPdfErrorMessage(e);
      } finally {
        this.pdfExporting = false;
      }
    },

    // responseType: 'blob' means even a JSON error body (e.g. a 400 for a bad sort value)
    // arrives as a Blob, not parsed JSON — unwrap it best-effort, falling back to the
    // generic message on any failure to parse (network error, non-JSON body, etc.).
    async _extractPdfErrorMessage(error) {
      const fallback = 'Could not generate PDF. Please try again.';
      try {
        if (error.response && error.response.data instanceof Blob) {
          const parsed = JSON.parse(await error.response.data.text());
          if (parsed && parsed.error) return parsed.error;
        }
      } catch (_) {
        // not JSON, or no response body — fall through to the generic message
      }
      return fallback;
    },

    hasScoreDrop(studentId) {
      const vs = this.vivaScores[String(studentId)];
      return vs ? this._isDropped(vs) : false;
    },

    _isDropped(vs) {
      // Use explicit property access rather than &&-chaining so that vivascore_z === 0
      // isn't treated as "no score" by the falsy short-circuit.
      const curr = vs.vivascore ? vs.vivascore.vivascore_z : null;
      const prev = vs.prev_vivascore_z;
      return curr !== null && curr !== undefined && prev !== null && prev !== undefined && curr < prev;
    },

    // Completed rows sorted by date_submitted; incomplete rows always pinned to the
    // bottom regardless of direction so they stay obvious at a glance.
    // Incomplete rows sit at the bottom for Earliest→Latest, but at the top for
    // Latest→Earliest — deliberately asymmetric, not a bug.
    _sortByCompletionTime(list, direction) {
      const complete = list.filter(s => s.submission_status === 'submitted' && s.date_submitted);
      const incomplete = list.filter(s => !(s.submission_status === 'submitted' && s.date_submitted));
      complete.sort((a, b) => {
        const diff = new Date(a.date_submitted) - new Date(b.date_submitted);
        return direction === 'asc' ? diff : -diff;
      });
      return direction === 'asc' ? complete.concat(incomplete) : incomplete.concat(complete);
    },

    // Scored rows sorted by vivascore_z; rows without a VivaScore always pinned to
    // the bottom regardless of direction, mirroring _sortByCompletionTime.
    _sortByVivaScore(list, direction) {
      const hasScore = s => {
        const vs = this.vivaScores[String(s.student_id)];
        return vs && vs.vivascore && vs.vivascore.available && vs.vivascore.vivascore_z !== null && vs.vivascore.vivascore_z !== undefined;
      };
      const scored = list.filter(hasScore);
      const unscored = list.filter(s => !hasScore(s));
      scored.sort((a, b) => {
        const za = this.vivaScores[String(a.student_id)].vivascore.vivascore_z;
        const zb = this.vivaScores[String(b.student_id)].vivascore.vivascore_z;
        return direction === 'desc' ? zb - za : za - zb;
      });
      return scored.concat(unscored);
    },

    _trendDelta(studentId) {
      // Explicit property access (not &&-chaining) so vivascore_z === 0 isn't treated as missing.
      const vs = this.vivaScores[String(studentId)];
      if (!vs) return null;
      const curr = vs.vivascore ? vs.vivascore.vivascore_z : null;
      const prev = vs.prev_vivascore_z;
      if (curr === null || curr === undefined || prev === null || prev === undefined) return null;
      return curr - prev;
    },

    trendArrow(studentId) {
      const delta = this._trendDelta(studentId);
      if (delta === null) return '';
      if (delta > 0) return '▲';
      if (delta < 0) return '▼';
      return '';
    },

    trendColor(studentId) {
      const delta = this._trendDelta(studentId);
      if (delta === null || delta === 0) return 'transparent';
      return delta > 0 ? 'var(--viva-green)' : 'var(--viva-orange)';
    },

    trendTitle(studentId) {
      const delta = this._trendDelta(studentId);
      if (delta === null || delta === 0) return '';
      return delta > 0 ? 'Score improved from previous assignment' : 'Score dropped from previous assignment';
    },

  }
};
</script>

<style scoped>
.student-submissions-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 100px;
}

.instructor-info {
  margin-bottom: 0;
  padding-bottom: 0;
  padding-top: 5px !important;
}

.submissions-container {
  max-width: 1400px;
  margin-left: 30px;
  margin-right: 30px;
  padding-left: 30px;
  padding-top: 15px;
}

.submissions-card {
  background: var(--viva-white);
  border: 3px solid var(--viva-green);
  border-radius: 12px;
  padding: 40px 40px 0 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.assignment-info-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--viva-green);
}

/* Shared Link Section */
.shared-link-section {
  background: rgba(236, 142, 0, 0.05);
  border: 2px solid var(--viva-orange);
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
}

.shared-link-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.shared-link-info {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.shared-link-info i {
  font-size: 28px;
  color: var(--viva-orange);
  margin-top: 2px;
}

.shared-link-info h4 {
  color: var(--viva-dark);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.shared-link-info p {
  color: var(--viva-grey);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.link-display-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.link-input {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid var(--viva-orange);
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  color: var(--viva-dark);
  background: var(--viva-white);
  cursor: pointer;
}

.link-input:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 0 3px rgba(236, 142, 0, 0.1);
}

.copy-btn {
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.link-note {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--viva-grey);
  font-size: 13px;
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid rgba(236, 142, 0, 0.2);
}

.link-note i {
  color: var(--viva-orange);
  font-size: 16px;
}

/* Responsive */
@media (max-width: 768px) {
  .link-display-container {
    flex-direction: column;
    align-items: stretch;
  }

  .copy-btn {
    width: 100%;
  }
}

.assignment-title {
  color: var(--viva-orange);
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 10px 0;
}

.course-name {
  color: var(--viva-dark);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.assignment-dates {
  color: var(--viva-orange);
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
}

.date-label {
  color: var(--viva-orange);
  font-weight: 700;
}

.loading-container {
  text-align: center;
  padding: 40px;
  color: var(--viva-grey);
}

.empty-state {
  text-align: center;
  padding: 60px 40px;
  color: var(--viva-grey);
}

.empty-state i {
  font-size: 48px;
  color: var(--viva-orange);
  margin-bottom: 20px;
}

.empty-state h3 {
  color: var(--viva-dark);
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 15px 0;
}

.empty-state p {
  font-size: 16px;
  margin: 0 0 25px 0;
}

.submissions-table-wrapper {
  margin-top: 20px;
  margin-bottom: 40px;
}

.sort-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  max-width: 50%;
}

.submission-count {
  font-size: 14px;
  font-weight: 600;
  color: var(--viva-dark);
  white-space: nowrap;
}

.pdf-export-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

.pdf-export-error {
  color: #dc3545;
  font-size: 13px;
  margin: 0;
}

/* Table scroll container */
.table-scroll-container {
  width: 100%;
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid var(--viva-green);
  border-radius: 8px;
}

/* Table styling */
.submissions-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--viva-white);
}

.submissions-table thead {
  background-color: var(--viva-green);
  color: var(--viva-white);
  position: sticky;
  top: 0;
  z-index: 10;
}

.submissions-table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  border-bottom: 2px solid var(--viva-green);
}

.submissions-table td {
  padding: 15px;
  border: 1px solid #e0e0e0;
  font-size: 14px;
}

/* Column widths */
.student-column {
  width: 20%;
}

/* Unused while the student-name link is commented out in the template; left in
   place for easy restoration once the Student Progress page launches. */
.student-name-link {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--viva-grey);
  text-underline-offset: 2px;
}

.student-name-link:hover {
  color: var(--viva-orange);
  text-decoration-color: var(--viva-orange);
}

.student-name {
  display: block;
  font-weight: 500;
}

.student-email {
  display: block;
  font-size: 11px;
  color: #888;
}

.status-column {
  width: 90px;
  text-align: center;
}

.date-column {
  width: 12%;
}

.comment-column {
  width: 140px;
  max-width: 140px;
  text-align: center;
}

.instructor-grade {
  display: block;
  font-weight: 600;
}

.instructor-comment-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  word-break: break-word;
  font-size: 12px;
  color: #6A6660;
}

.note-column {
  width: 120px;
  max-width: 120px;
}

.note-indicator {
  cursor: pointer;
  color: var(--viva-orange);
  display: -webkit-box;
  -webkit-line-clamp: 3;  /* Show 3 lines before ellipsis */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  word-break: break-word;
}

.note-indicator:hover {
  color: var(--viva-green);
}

.accommodation-column {
  width: 6%;
  text-align: center;
}

.accommodation-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: #e9ecef;
  color: #6c757d;
}

.accommodation-badge.active {
  background-color: var(--viva-green);
  color: white;
}

.accommodation-badge.inactive {
  opacity: 0.25;
}

/* Accommodation toggles in modals */
.accommodation-toggles-modal {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.accommodation-btn {
  padding: 10px 18px;
  border-radius: 6px;
  border: 2px solid #6c757d;
  background-color: #f8f9fa;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.accommodation-btn:hover {
  background-color: #e2e6ea;
  border-color: #5a6268;
}

.accommodation-btn.active {
  background-color: var(--viva-green);
  border-color: var(--viva-green);
  color: white;
}

.accommodation-btn.active:hover {
  background-color: #5a9f5f;
  border-color: #5a9f5f;
}

/* Video icon styling */
.video-icon {
  width: 32px;
  height: 32px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.video-icon:hover {
  transform: scale(1.15);
}

/* Row styling based on submission status */
.submissions-table tbody tr.submitted-row td {
  font-weight: 600;
  color: var(--viva-dark);
}

.submissions-table tbody tr.not-submitted-row td {
  color: var(--viva-grey);
  font-weight: 400;
}

/* Not sent row styling - more muted appearance */
.submissions-table tbody tr.not-sent-row {
  opacity: 0.6;
  background-color: #f9f9f9;
  cursor: pointer;
}

.submissions-table tbody tr.not-sent-row:hover {
  opacity: 0.75;
  background-color: #f5f5f5;
}

/* Override opacity for badge and button in not-sent rows */
.submissions-table tbody tr.not-sent-row .not-sent-badge,
.submissions-table tbody tr.not-sent-row .send-btn {
  opacity: 1;
}

/* Late submission styling */
.submissions-table tbody tr td.late-submission {
  color: #dc3545;
  font-weight: 700;
  background-color: #f8d7da;
}

.submissions-table tbody tr:hover {
  background-color: #f5f5f5;
}

/* Modal close button */
.viva-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.viva-modal-header .close {
  background: none;
  border: none;
  font-size: 24px;
  font-weight: bold;
  color: white;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  position: absolute;
  top: 15px;
  right: 20px;
}

.viva-modal-header .close:hover {
  color: #f0f0f0;
}

/* Assignment header with button */
.assignment-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

/* Actions column */
.actions-column {
  width: 100px;
  text-align: center;
}

.action-btn {
  background: none;
  border: 2px solid #B0ACAA;
  border-radius: 6px;
  color: #B0ACAA;
  cursor: pointer;
  padding: 6px 10px;
  margin: 0 3px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background-color: #B0ACAA;
  color: var(--viva-white);
  transform: scale(1.05);
}

.action-btn .glyphicon {
  font-size: 14px;
}

.resend-btn:hover {
  background-color: #B0ACAA;
}

.send-btn {
  border-color: var(--viva-orange);
  color: var(--viva-orange);
}

.send-btn:hover {
  background-color: var(--viva-orange);
  color: var(--viva-white);
}

/* Not sent badge */
.not-sent-badge {
  display: inline-block;
  padding: 4px 12px;
  background-color: var(--viva-orange);
  color: var(--viva-white);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Status column: pink "Not Complete" oval shown when the viva wasn't taken */
.status-not-complete-badge {
  display: inline-block;
  padding: 4px 12px;
  background-color: #FBD9E1;
  color: #D0021B;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

/* Read-only input styling */
.form-input:read-only {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

/* Reset button styling */
.reset-btn {
  border-color: var(--viva-orange);
  color: var(--viva-orange);
}

.reset-btn:hover {
  background-color: var(--viva-orange);
  color: var(--viva-white);
}

/* Warning box in reset modal */
.warning-box {
  background-color: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
}

.warning-icon {
  font-size: 36px;
  margin: 0 0 10px 0;
}

.warning-text {
  margin: 0;
  color: #856404;
  font-size: 16px;
}

/* Accommodation options styling */
.accommodation-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 10px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--viva-dark);
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.time-multiplier-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label-small {
  font-size: 13px;
  font-weight: 600;
  color: var(--viva-dark);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-select {
  padding: 8px 12px;
  border: 2px solid var(--viva-green);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--viva-dark);
  background-color: var(--viva-white);
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:hover {
  border-color: var(--viva-orange);
}

.form-select:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 0 3px rgba(255, 123, 0, 0.1);
}

/* VivaScore table columns */
.vivascore-col-header,
.metrics-col-header,
.flag-col-header {
  white-space: nowrap;
  font-size: 12px;
}

.col-info-icon {
  font-size: 11px;
  color: var(--viva-grey);
  cursor: help;
}

.vivascore-col {
  width: 80px;
  text-align: center;
}

.metrics-col {
  width: 120px;
  text-align: center;
}

.flag-col {
  width: 40px;
  text-align: center;
}

.vivascore-z {
  font-weight: 700;
  font-size: 14px;
}

.vivascore-col .metric-bar,
.metrics-col .metric-bar {
  margin-top: 4px;
}

.vivascore-na {
  color: var(--viva-grey);
  font-size: 14px;
}

.vivascore-spinner {
  color: var(--viva-grey);
}

.drop-badge {
  color: var(--viva-orange);
  font-size: 13px;
  font-weight: 700;
  cursor: default;
}

/* Active overlay row highlight */
.submissions-table tbody tr.active-overlay-row {
  background-color: #f0f7ff;
}

.submissions-table tbody tr.active-overlay-row:hover {
  background-color: #e6f2ff;
}

/* Class Trajectory section */
.trajectory-container {
  padding-top: 20px;
  margin-bottom: 30px;
}

.trajectory-card {
  background: var(--viva-white);
  border: 3px solid var(--viva-green);
  border-radius: 12px;
  padding: 30px 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.trajectory-title {
  color: var(--viva-orange);
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.trajectory-subtitle {
  color: var(--viva-grey);
  font-size: 13px;
  margin: 0 0 20px 0;
}

.legend-instructions {
  font-size: 11px;
  color: var(--viva-grey);
  line-height: 1.4;
  margin: 4px 0 2px;
}

.violin-legend-wrap {
  position: relative;
}

/* Fade on right edge signals more content to scroll */
.violin-legend-wrap::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 100%;
  background: linear-gradient(to right, transparent, var(--viva-white));
  pointer-events: none;
}

.violin-legend {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  align-items: center;
  gap: 8px;
  padding: 10px 0 8px;
  scrollbar-width: auto;
}

.legend-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  border: 1.5px solid var(--chip-color, #222222);
  color: var(--chip-color, #222222);
  background: transparent;
  transition: background 0.15s;
  user-select: none;
}

.legend-chip:hover {
  background: rgba(0, 0, 0, 0.05);
}

.legend-chip--active {
  background: var(--chip-color, #222222);
  color: #ffffff;
}

.legend-chip--avg {
  --chip-color: #222222;
}

.notify-students-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  align-self: center;
  color: var(--viva-grey);
}

.notify-students-label input[type="checkbox"] {
  accent-color: var(--viva-orange);
}

</style>
